import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, concatMap } from 'rxjs/operators';
import {
  ChangeUserPasswordInfo,
  CheckNewPassword,
  InitiatePasswordChange,
  InitiateSsoSession,
  LoginOAuthParams
} from '../../../models';
import { CheckNewPasswordService } from '../../multi-factor-authentication-adapter-service/check-new-password/check-new-password.service';
import { InitiatePasswordChangeService } from '../../multi-factor-authentication-adapter-service/initiate-password-change/initiate-password-change.service';
import { InitiateSsoSessionService } from '../../multi-factor-authentication-adapter-service/initiate-sso-session/initiate-sso-session.service';
import { MfaUtilService } from '../../multi-factor-authentication-adapter-service/util-service/mfa-util.service';

@Injectable({
  providedIn: 'root'
})
export class ChangeUserPasswordService {
  private InitiateSsoSession = inject(InitiateSsoSessionService);
  private initiatePasswordChange = inject(InitiatePasswordChangeService);
  private checkNewPassword = inject(CheckNewPasswordService);
  private mfaUtils = inject(MfaUtilService);

  changeUserPassword(
    loginOAuthParams: LoginOAuthParams,
    fieldValues: ChangeUserPasswordInfo
  ): Observable<CheckNewPassword | string> {
    return this.InitiateSsoSession.initiateSsoSession(
      loginOAuthParams,
      'CHANGE_PASSWORD'
    ).pipe(
      concatMap((response: InitiateSsoSession) => {
        return this.initiatePasswordChange
          .initiatePasswordChange(
            loginOAuthParams.oauthConfig,
            response.id ? response.id : ''
          )
          .pipe(
            concatMap((response: InitiatePasswordChange) => {
              return this.checkNewPassword.checkNewPassword(
                loginOAuthParams.oauthConfig,
                response.id ? response.id : '',
                fieldValues
              );
            })
          );
      }),
      catchError((error) => {
        return of(this.checkResetPasswordError(error));
      })
    );
  }

  checkResetPasswordError(
    response: CheckNewPassword
  ): CheckNewPassword | string {
    if (
      this.checkFailValidators(response) ||
      this.checkPasswordChangeErrorNoValidators(response)
    ) {
      return response;
    } else {
      return this.mfaUtils.getMfaRegistrationError(response);
    }
  }

  checkFailValidators(response: CheckNewPassword): boolean | null | undefined {
    return (
      response?.error?.details &&
      response?.error?.details[0]?.code === 'PASSWORD_CHANGE_ERROR' &&
      response?.error?.details[0]?.failedValidators &&
      response?.error?.details[0]?.failedValidators.length > 0
    );
  }

  checkPasswordChangeErrorNoValidators(
    response: CheckNewPassword
  ): boolean | null | undefined {
    return (
      response?.error?.details &&
      response?.error?.details[0]?.code === 'PASSWORD_CHANGE_ERROR' &&
      !!response?.error?.details[0]?.userMessage
    );
  }
}
